import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { matchSymbols, type SymbolOption } from './symbolSearch';

/** Typing narrows the list; this is only a ceiling so a huge catalogue cannot flood the page. */
const MAX_SHOWN = 1000;

/**
 * A searchable symbol field (the ARIA combobox pattern): tap or click it (or its arrow) to see every symbol, type to
 * filter, arrows to move, Enter to choose, Escape to leave it as it was. When not being edited it shows the chosen symbol.
 */
export function SymbolPicker({
  id,
  value,
  options,
  onSelect,
  className,
}: {
  id: string;
  value: string;
  options: readonly SymbolOption[];
  onSelect: (name: string) => void;
  className: string;
}) {
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{
    left: number;
    top: number;
    width: number;
    maxHeight: number;
    host: Element;
  } | null>(null);
  const [query, setQuery] = useState<string | null>(null); // null: not searching, the field shows the symbol
  const [browsing, setBrowsing] = useState(false); // the whole list is open (a tap, with nothing typed yet)
  const [active, setActive] = useState(0);
  const open = query !== null || browsing;
  const matches = !open
    ? []
    : query !== null
      ? matchSymbols(options, query, MAX_SHOWN)
      : options.slice(0, MAX_SHOWN);
  const at = Math.min(active, Math.max(0, matches.length - 1));

  const close = () => {
    setQuery(null);
    setBrowsing(false);
  };
  const choose = (name: string | undefined) => {
    if (name) onSelect(name);
    close();
  };
  /** Opens the whole list with the current symbol highlighted, so a long list starts where the user already is. */
  const browse = () => {
    setActive(
      Math.max(
        0,
        options.findIndex((o) => o.name === value),
      ),
    );
    setBrowsing(true);
  };

  // The list is drawn outside the field's own box (a toolbar around it may clip), so it is placed by hand: under the field,
  // at least as wide as a symbol and its name need, and never off the screen. A closed list is not drawn at all, and
  // reopening measures again before the browser paints, so a stale place is never shown.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const box = wrapper.current?.getBoundingClientRect();
      if (!box) return;
      const width = Math.min(Math.max(box.width, 260), window.innerWidth - 16);
      setPlace({
        width,
        left: Math.max(8, Math.min(box.left, window.innerWidth - width - 8)),
        top: box.bottom + 4,
        maxHeight: Math.max(120, Math.min(window.innerHeight * 0.5, window.innerHeight - box.bottom - 16)),
        // Inside the dialog when the field is in one: a modal dialog sits in the top layer, above anything else on the page.
        host: wrapper.current?.closest('dialog') ?? document.body,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  // Keep the highlighted symbol in view as the list opens and as the arrows move through it.
  useEffect(() => {
    if (open) document.getElementById(`${listId}-${at}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, at, listId]);

  return (
    <div className="relative" ref={wrapper}>
      <input
        ref={input}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && matches[at] ? `${listId}-${at}` : undefined}
        autoComplete="off"
        spellCheck={false}
        className={`${className} pr-7`}
        value={query ?? value}
        onFocus={(e) => {
          e.target.select();
          browse();
        }}
        onClick={() => {
          if (!open) browse(); // tapping the field again, after the list was closed, opens it again
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
        onBlur={close}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) return browse();
            const n = matches.length;
            if (n > 0) setActive((at + (e.key === 'ArrowDown' ? 1 : n - 1)) % n);
          } else if (e.key === 'Enter') {
            if (open) {
              e.preventDefault();
              choose(matches[at]?.name);
            }
          } else if (e.key === 'Escape' && open) {
            e.preventDefault();
            e.stopPropagation(); // a dialog around the field must not close as well
            close();
          }
        }}
      />
      {/* The arrow opens (or closes) the full list, like a <select>'s own: on a phone there is no arrow key to do it. */}
      <button
        type="button"
        tabIndex={-1}
        aria-label={open ? 'Close the symbol list' : 'Show all symbols'}
        onMouseDown={(e) => e.preventDefault()} // keep the field's focus
        onClick={() => {
          if (open) return close();
          input.current?.focus();
          browse();
        }}
        className="absolute right-0 top-0 grid h-full w-8 place-items-center text-xs text-muted"
      >
        <span aria-hidden="true">▾</span>
      </button>
      {open &&
        place &&
        createPortal(
          <ul
            id={listId}
            role="listbox"
            aria-label="Symbols"
            style={{
              position: 'fixed',
              left: place.left,
              top: place.top,
              width: place.width,
              maxHeight: place.maxHeight,
            }}
            className="z-[1000] overflow-auto rounded-md border border-line bg-panel py-1 text-sm text-fg shadow-2xl"
          >
            {matches.length === 0 && <li className="px-2 py-1 text-muted">No symbol matches.</li>}
            {matches.map((o, i) => (
              <li
                key={o.name}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === at}
                onMouseDown={(e) => e.preventDefault()} // keep focus in the field until the click lands
                onClick={() => choose(o.name)}
                onMouseEnter={() => setActive(i)}
                className={`flex min-h-9 cursor-pointer items-center justify-between gap-3 px-2 py-1 ${i === at ? 'bg-panel2' : ''}`}
              >
                <span className={`font-semibold ${o.name === value ? 'text-accent' : ''}`}>{o.name}</span>
                {o.description && <span className="truncate text-xs text-muted">{o.description}</span>}
              </li>
            ))}
          </ul>,
          place.host,
        )}
    </div>
  );
}
